import React, { useState, useEffect } from 'react';
import {
  Typography,
  Form,
  Select,
  notification,
  Input,
  Space,
  Descriptions,
  Alert,
  Skeleton,
  Layout,
  Card,
  Table,
  Modal,
} from 'antd';

import CustomButton from '../../../components/CustomButton';
import {
  put,
  get,
  announcementInfo,
  getValidationDocuments as getBasicDocuments,
  sendFinalStatus,
} from '../../../api/module/announcement_preselection';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { filter as getTraining } from '../../../api/module/resume_training';
import { filter as getExperience } from '../../../api/module/resume_experience';
import { get as getDocumentUrl } from '../../../api/module/document_repository';
import { useParams, useLocation } from 'react-router-dom';
import { catchExceptionSRate } from '../../../utils/helpers/helpers';
import PdfViewer from '../../../components/PdfViewer';
import { ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { IconButton } from '../../../components';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Content } = Layout;

const tailLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

const AnnouncementPreselectionValidationForm = ({ history }) => {
  const location = useLocation();
  const { announcement_id, announcement_rol_id, preselection_id } = useParams();
  const [loadingForm, setLoadingForm] = useState(true);
  const [info, setInfo] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(null);

  const [tab, setTab] = useState(undefined);

  const [tabData, setTabData] = useState([]);
  const [changed, setChanged] = useState(false);

  const [isSaving, setSaving] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const [url, setUrl] = useState(null);
  const [showPDF, setShowPDF] = useState(false);

  const [finalStatus, setFinalStatus] = useState('');
  const [loadingFinalStatus, setLoadingFinalStatus] = useState(false);

  const stateOpt = [
    { value: 24, label: 'Pendiente' },
    { value: 25, label: 'Cumple' },
    { value: 26, label: 'No cumple' },
    { value: 109, label: 'No revisado' },
    { value: 110, label: 'No aplica' },
  ];

  const breadcrumbItems = [
    {
      text: 'Preselección',
      to: `${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION}`,
    },
    {
      text: 'Validación',
      to: `${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION_VALIDATION}/${announcement_id}/${announcement_rol_id}`,
    },
    {
      text: 'Prevalidación documentos',
    },
  ];

  useEffect(() => {
    getAnnouncement();
    getPreselection();
  }, []);

  useEffect(() => {
    if (initialValues) getFinalStatus();
  }, [initialValues]);

  const getAnnouncement = async () => {
    try {
      const { data: aData } = await announcementInfo(announcement_rol_id);
      setInfo(aData);
      setLoadingForm(false);
    } catch (err) {
      console.log(err);
    }
  };

  const getPreselection = async () => {
    try {
      const { data } = await get(preselection_id);
      setInitialValues(data);
    } catch (err) {
      console.log(err);
    }
  };

  const showDocument = async (documento_nombre) => {
    setLoadingDocument(true);
    const response = await getDocumentUrl(documento_nombre);

    window.open(response.data.url);

    setLoadingDocument(false);
  };

  const getDocument = async (documentId) => {
    setUrl(null);
    const response = await getDocumentUrl(`${documentId}.pdf`);
    setUrl(response.data.url);
    setShowPDF(true);
    setLoadingDocument(null);
  };

  const changeTab = async (newTab) => {
    setTabLoading(true);

    try {
      let dataSet;
      if (newTab === 'documentos')
        ({ data: dataSet } = await getBasicDocuments(initialValues.tercero));
      else if (newTab === 'educacion')
        ({ data: dataSet } = await getTraining(
          '?filter[tercero]=' + initialValues.tercero,
        ));
      else
        ({ data: dataSet } = await getExperience(
          '?filter[tercero]=' + initialValues.tercero,
        ));
      setTabData(dataSet.results);
      setTab(newTab);
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_GET_MSG,
        duration: 2,
      });
    }

    setTabLoading(false);
  };

  const checkChanged = (newTab) => {
    if (changed) {
      Modal.confirm({
        title: MESSAGES.MSG_UNSAVED_CHANGES,
        icon: <ExclamationCircleOutlined />,
        content: MESSAGES.MSG_UNSAVED_MSG,
        okText: 'Si',
        cancelText: 'Volver',
        onOk: () => {
          changeTab(newTab);
          setChanged(false);
        },
      });
    } else {
      changeTab(newTab);
    }
  };

  const back = () => {
    history.push({
      pathname: `${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION_VALIDATION}/${announcement_id}/${announcement_rol_id}`,
      search: location.state?.fromSearch || '',
    });
  };

  const onFinish = async (data) => {
    setSaving(true);
    try {
      setTabLoading(true);
      const fullData = {
        ...initialValues,
        ...data,
      };
      const { data: newData } = await put(preselection_id, fullData);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setTab(undefined);
      setChanged(false);
      setInitialValues((s) => ({ ...s, ...newData }));
      setTabLoading(false);
      setSaving(false);
    } catch (err) {
      catchExceptionSRate(err, MESSAGES.DESC_ERROR_POST_MSG);
      setTabLoading(false);
    }
  };

  const getFinalStatus = (skipPending = true) => {
    let moduleStatus;
    if (initialValues.tipo_persona === 1) {
      moduleStatus = [
        initialValues.estado_documentos,
        initialValues.estado_educacion,
        initialValues.estado_experiencia,
      ];
    } else {
      moduleStatus = [initialValues.estado_documentos];
    }
    if (!skipPending && moduleStatus.some((i) => i == null)) {
      return false;
    }
    if (moduleStatus.includes(26)) {
      setFinalStatus('Rechazado');
      return true;
    }
    if (moduleStatus.includes(24)) {
      setFinalStatus('Pendiente');
      return true;
    }
    setFinalStatus('Preseleccionado');
    return true;
  };

  const saveFinalStatus = async (skipWarning = false, skipPending = false) => {
    const hasPending = getFinalStatus(false) === false;
    if (hasPending) {
      if (finalStatus !== 'Preseleccionado') {
        Modal.error({
          title: 'Aviso',
          content:
            'Hay módulos sin revisar. No se puede guardar el estado final.',
        });
        return;
      } else if (!skipPending) {
        Modal.confirm({
          title: 'Aviso',
          content:
            'Hay módulos sin revisar. ¿Desea marcarlos con el estado "No revisado" y Preseleccionar el registro?',
          okText: 'Si',
          cancelText: 'No',
          onOk: () => saveFinalStatus(skipWarning, true),
        });
        return;
      }
    }
    if (!skipWarning && finalStatus === 'Rechazado') {
      Modal.confirm({
        title: 'Aviso',
        content:
          'El estado final es Rechazado, si lo guarda, esta persona no podrá continuar con el proceso de Selección y no se podrá habilitar de nuevo ¿Está seguro(a) de guardar este registro?',
        okText: 'Si',
        cancelText: 'No',
        onOk: () => saveFinalStatus(true, skipPending),
      });
      return;
    }
    try {
      setLoadingFinalStatus(true);
      await sendFinalStatus(initialValues.id, finalStatus);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setTimeout(() => back(), MESSAGES.TIME);
    } catch (error) {
      catchExceptionSRate(error);
      setLoadingFinalStatus(false);
    }
  };

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Módulo de Preselección
      </Title>
      <Skeleton loading={loadingForm} active>
        {info ? (
          <Descriptions
            layout="vertical"
            title="Información de convocatoria"
            column={2}
            bordered
          >
            <Descriptions.Item label="Número de convocatoria">
              {info.convocatoria_numero}
            </Descriptions.Item>
            <Descriptions.Item label="Nombre de la convocatoria">
              {info.convocatoria_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Cargo/Rol" span={2}>
              {info.cargo}
            </Descriptions.Item>
            <Descriptions.Item
              label="Otras observaciones para el cargo"
              span={2}
            >
              {info.observaciones_cargo}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        {initialValues && (
          <>
            <Descriptions
              layout="vertical"
              title="¿Qué documentos desea validar?"
              column={5}
              bordered
              labelStyle={{ display: 'block', textAlign: 'center' }}
              contentStyle={{ display: 'block', textAlign: 'center' }}
              style={{ marginTop: '15px' }}
            >
              <Descriptions.Item
                label={
                  <CustomButton
                    text="Básicos obligatorios"
                    type={tab === 'documentos' ? 'primary' : 'default'}
                    onClick={() => checkChanged('documentos')}
                  />
                }
              >
                <Space direction={'vertical'}>
                  <Text>Estado de revisión:</Text>
                  <Text strong>
                    {initialValues.estado_documentos
                      ? stateOpt.find(
                          (e) => e.value === initialValues.estado_documentos,
                        ).label
                      : 'Por revisar'}
                  </Text>
                </Space>
              </Descriptions.Item>
              {initialValues.tipo_persona === 1 ? (
                <>
                  <Descriptions.Item
                    label={
                      <CustomButton
                        text="Educación/Formación"
                        type={tab === 'educacion' ? 'primary' : 'default'}
                        onClick={() => checkChanged('educacion')}
                      />
                    }
                  >
                    <Space direction={'vertical'}>
                      <Text>Estado de revisión:</Text>
                      <Text strong>
                        {initialValues.estado_educacion
                          ? stateOpt.find(
                              (e) => e.value === initialValues.estado_educacion,
                            ).label
                          : 'Por revisar'}
                      </Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <CustomButton
                        text="Experiencia"
                        type={tab === 'experiencia' ? 'primary' : 'default'}
                        onClick={() => checkChanged('experiencia')}
                      />
                    }
                  >
                    <Space direction={'vertical'}>
                      <Text>Estado de revisión:</Text>
                      <Text strong>
                        {initialValues.estado_experiencia
                          ? stateOpt.find(
                              (e) =>
                                e.value === initialValues.estado_experiencia,
                            ).label
                          : 'Por revisar'}
                      </Text>
                    </Space>
                  </Descriptions.Item>
                </>
              ) : null}
              <Descriptions.Item label="Estado final">
                <Space direction={'vertical'}>
                  <Text strong>{finalStatus}</Text>
                  <CustomButton
                    text="Guardar"
                    type="primary"
                    onClick={() => saveFinalStatus()}
                    loading={loadingFinalStatus}
                    disabled={tabLoading}
                  />
                </Space>
              </Descriptions.Item>
            </Descriptions>
            <Skeleton loading={tabLoading} active>
              {tab ? (
                <>
                  {tab == 'documentos' ? (
                    <></>
                  ) : tab === 'educacion' ? (
                    <Descriptions
                      layout="vertical"
                      title="Requisitos de la convocatoria para el cargo"
                      column={1}
                      bordered
                    >
                      <Descriptions.Item label="Tipo de educación">
                        {info.estudios}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Descriptions
                      layout="vertical"
                      title="Requisitos de la convocatoria para el cargo"
                      column={1}
                      bordered
                    >
                      <Descriptions.Item label="Tipo de experiencia">
                        {info.tipo_experiencia_laboral}
                      </Descriptions.Item>
                      <Descriptions.Item label="Experiencia (meses)">
                        {info.tiempo_experiencia_laboral}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {initialValues.cargo_aplicado !== null && (
                    <Alert
                      style={{ marginBottom: 10 }}
                      message="Atención"
                      description={`El usuario también se encuentra aplicando al cargo ${initialValues.cargo_aplicado} de esta convocatoria. ¿Desea continuar con el proceso para este cargo?. En caso que no continúe, por favor rechazarlo.`}
                      type="error"
                      showIcon
                    />
                  )}
                  <Card
                    size={'small'}
                    bordered={false}
                    title={
                      <Title level={3}>{initialValues.identificacion}</Title>
                    }
                  >
                    {tab === 'documentos' ? (
                      <>
                        <Table
                          columns={[
                            {
                              title: 'Tipo documento',
                              dataIndex: 'tipo_documento_descripcion',
                            },
                            {
                              title: 'Documento',
                              dataIndex: 'documento',
                              align: 'center',
                              render: (t) =>
                                t ? (
                                  <IconButton
                                    loading={loadingDocument === t}
                                    title="Ver documento"
                                    bordered
                                    color="blue"
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                      setLoadingDocument(t);
                                      getDocument(t);
                                    }}
                                  />
                                ) : (
                                  'No cargado'
                                ),
                            },
                          ]}
                          dataSource={tabData}
                          rowKey="id"
                        />
                        <PdfViewer
                          pdf={url}
                          open={showPDF}
                          onCancel={() => {
                            setShowPDF(false);
                          }}
                        />
                      </>
                    ) : (
                      tabData.map((item, index) =>
                        tab === 'educacion' ? (
                          <Card
                            size={'small'}
                            bordered={false}
                            title={
                              <Title level={5} className={'title-default'}>
                                Educación No. {index + 1}
                              </Title>
                            }
                          >
                            <Descriptions layout="vertical" column={3} bordered>
                              <Descriptions.Item
                                label={
                                  item.tipo_educacion === 1 && item.finalizado
                                    ? 'Título obtenido '
                                    : 'Nombre del programa'
                                }
                              >
                                {item.titulo_nombre_programa}
                              </Descriptions.Item>
                              <Descriptions.Item label="Estado">
                                {item.finalizado === false
                                  ? 'En curso'
                                  : 'Finalizado'}
                              </Descriptions.Item>
                              {item.tipo_educacion === 2 &&
                              item.finalizado === false ? null : (
                                <Descriptions.Item
                                  label={
                                    item.tipo_educacion === 1 && item.finalizado
                                      ? 'Fecha de grado '
                                      : item.tipo_educacion === 1 &&
                                          !item.finalizado
                                        ? 'Semestre actual'
                                        : 'Fecha de terminación'
                                  }
                                >
                                  {item.tipo_educacion === 1 && !item.finalizado
                                    ? item.semestre
                                    : item.fecha_terminacion}
                                </Descriptions.Item>
                              )}
                              <Descriptions.Item label="Certificado">
                                {item.documento_certificado_nombre ? (
                                  <CustomButton
                                    htmlType={'button'}
                                    loading={loadingDocument}
                                    onClick={() => {
                                      showDocument(
                                        item.documento_certificado_nombre,
                                      );
                                    }}
                                    text={'Ver documento'}
                                  />
                                ) : (
                                  <Text>No se encontró documento</Text>
                                )}
                              </Descriptions.Item>
                              <Descriptions.Item label="Tarjeta profesional">
                                {item.documento_tarjeta_profesional_nombre ? (
                                  <CustomButton
                                    htmlType={'button'}
                                    loading={loadingDocument}
                                    onClick={() => {
                                      showDocument(
                                        item.documento_tarjeta_profesional_nombre,
                                      );
                                    }}
                                    text={'Ver documento'}
                                  />
                                ) : (
                                  <Text>No se encontró documento</Text>
                                )}
                              </Descriptions.Item>
                            </Descriptions>
                          </Card>
                        ) : (
                          <Card
                            size={'small'}
                            bordered={false}
                            title={
                              <Title level={5} className={'title-default'}>
                                Experiencia No. {index + 1}
                              </Title>
                            }
                          >
                            <Descriptions layout="vertical" column={3} bordered>
                              <Descriptions.Item label="Cargo">
                                {item.cargo}
                              </Descriptions.Item>
                              <Descriptions.Item label="Area de experiencia">
                                {item.area_experiencia_nombre}
                              </Descriptions.Item>
                              <Descriptions.Item label="Tipo de cargo">
                                {item.tipo_cargo_nombre}
                              </Descriptions.Item>
                              <Descriptions.Item label="Estado">
                                {item.finalizado === false
                                  ? 'Actual'
                                  : 'Finalizado'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Fecha de Ingreso">
                                {item.fecha_ingreso}
                              </Descriptions.Item>
                              <Descriptions.Item label="Fecha de terminación">
                                {item.fecha_terminacion}
                              </Descriptions.Item>
                              <Descriptions.Item label="Meses de trabajo">
                                {item.meses.toFixed(2)}
                              </Descriptions.Item>
                              <Descriptions.Item label="Certificado">
                                {item.documento_nombre ? (
                                  <CustomButton
                                    loading={loadingDocument}
                                    onClick={() => {
                                      showDocument(item.documento_nombre);
                                    }}
                                    text={'Ver documento'}
                                  />
                                ) : (
                                  <Text>No se encontró documento</Text>
                                )}
                              </Descriptions.Item>
                            </Descriptions>
                          </Card>
                        ),
                      )
                    )}
                  </Card>
                  <Card size={'small'} bordered={false}>
                    <Form
                      layout="vertical"
                      onFinish={onFinish}
                      initialValues={initialValues}
                      onValuesChange={() => setChanged(true)}
                    >
                      <Form.Item
                        name={`estado_${tab}`}
                        label={`Estado preselección ${tab}`}
                        rules={[{ required: true }]}
                      >
                        <Select
                          disabled={initialValues.estado_tercero === 12}
                          options={
                            tab === 'educacion' ||
                            initialValues.tipo_persona === 2
                              ? stateOpt.filter((i) => i.value !== 110)
                              : stateOpt
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues[`estado_${tab}`] !==
                          currentValues[`estado_${tab}`]
                        } // Dynamically adds/removes the red asterisk (dependencies prop does not re-render)
                      >
                        {({ getFieldValue }) => {
                          const isRequired = [24, 26, 109, 110].includes(
                            getFieldValue(`estado_${tab}`),
                          );
                          return (
                            <Form.Item
                              {...tailLayout}
                              name={`observacion_${tab}`}
                              label="Observación general"
                              required={isRequired} // Dynamically adds/removes the red asterisk
                              rules={[
                                {
                                  required: isRequired,
                                },
                              ]}
                            >
                              <TextArea
                                disabled={initialValues.estado_tercero === 12}
                                rows={4}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                      <Space>
                        {initialValues.estado_tercero !== 12 && (
                          <CustomButton loading={isSaving} text="Guardar" />
                        )}
                        <CustomButton
                          type={'default'}
                          className={'back-button'}
                          htmlType="button"
                          onClick={back}
                          text="Volver"
                        />
                      </Space>
                    </Form>
                  </Card>
                </>
              ) : (
                <CustomButton
                  type={'default'}
                  className={'back-button'}
                  htmlType="button"
                  onClick={back}
                  text="Volver"
                />
              )}
            </Skeleton>
          </>
        )}
      </Skeleton>
    </Content>
  );
};

export default AnnouncementPreselectionValidationForm;
